import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { API_BASE_URL, apiClient } from '@/services/api-client';
import { useAuth } from '@/hooks/useAuth';
import type { LegalInvoiceListItem, LegalInvoiceSeriesFilter, LegalInvoiceSourceFilter } from '../types';

function normalizeInvoiceRow(row: Record<string, unknown>): LegalInvoiceListItem {
    return {
        id: String(row.id),
        invoiceNumber: String(row.invoice_number ?? row.invoiceNumber ?? ''),
        series: String(row.series ?? ''),
        documentType: String(row.document_type ?? row.documentType ?? 'invoice'),
        status: String(row.status ?? ''),
        sourceType: String(row.source_type ?? row.sourceType ?? ''),
        sourceId: String(row.source_id ?? row.sourceId ?? ''),
        customerName: String(row.customer_name ?? row.customerName ?? ''),
        customerEmail: String(row.customer_email ?? row.customerEmail ?? ''),
        customerTaxId: row.customer_tax_id != null ? String(row.customer_tax_id) : null,
        taxRegime: String(row.tax_regime ?? row.taxRegime ?? ''),
        taxBaseCents: Number(row.tax_base_cents ?? row.taxBaseCents ?? 0),
        taxAmountCents: Number(row.tax_amount_cents ?? row.taxAmountCents ?? 0),
        totalCents: Number(row.total_cents ?? row.totalCents ?? 0),
        currency: String(row.currency ?? 'eur'),
        issuedAt: String(row.issued_at ?? row.issuedAt ?? ''),
        pdfGeneratedAt: row.pdf_generated_at != null ? String(row.pdf_generated_at) : null,
        storeOrderNumber: row.store_order_number != null ? String(row.store_order_number) : null,
        userId: row.user_id != null ? String(row.user_id) : null,
    };
}

export function useLegalInvoicesList(
    series: LegalInvoiceSeriesFilter,
    sourceType: LegalInvoiceSourceFilter,
    search: string
) {
    const { user } = useAuth();
    return useQuery<LegalInvoiceListItem[]>({
        queryKey: ['admin', 'legal-invoices', series, sourceType, search],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const params: Record<string, unknown> = {
                p_admin_user_id: user!.id,
                p_limit: 200,
            };
            if (series !== 'all') params.p_series = series;
            if (sourceType !== 'all') params.p_source_type = sourceType;
            if (search.trim()) params.p_search = search.trim();

            const { data, error } = await dbClient.rpc('admin_list_legal_invoices', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as Record<string, unknown>[];
            return rows.map(normalizeInvoiceRow);
        },
        staleTime: 30_000,
    });
}

export async function downloadLegalInvoicePdf(invoiceId: string, invoiceNumber: string): Promise<void> {
    const session = apiClient.getSession();
    if (!session?.access_token) {
        throw new Error('Sesión no válida');
    }

    const url = `${API_BASE_URL}/billing/admin/invoice/${invoiceId}/pdf?regenerate=1`;
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (!res.ok) {
        let msg = 'Error al descargar la factura';
        try {
            const err = await res.json();
            msg = err?.message || err?.error || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${invoiceNumber.replace(/[^a-zA-Z0-9\-]/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}
